import type {
  CreateGroomingBatchBody,
  CreateGroomingBatchResponse as ContractCreateGroomingBatchResponse,
  GroomingBatch as ContractGroomingBatch,
  GroomingBatchMode,
  GroomingBatchStatus,
  GroomingCandidatesQuery,
  GroomingCandidatesResponse,
  GroomingConfigResponse,
  GroomingRepoInput,
  GroomingStage,
  GroomingStopReason,
  ProductAreaSource,
} from "@ttc-spectree/toolbox-contract";
import type {
  AdoAreaPathList,
  FogbugzProjectLinkInput,
  FogbugzProjectLinksList,
  FogbugzProjectLinkView,
} from "@ttc-spectree/toolbox-contract/fogbugz";
import { api } from "./client";
import type { ReadinessCriterion, ReadinessMissing, ReadinessTier, WorkItemKind, WorkItemRef } from "./work-items";

/**
 * Client for the grooming workspace API (`/api/v1/grooming`, epic
 * PI-SPEC-SW-E279, features PI-SPEC-SW-1388 and PI-SPEC-SW-1395). The held
 * brief routes (PI-SPEC-SW-1387) live on the work-item API and are in
 * `./work-items.ts`.
 *
 * The shapes the batch engine also reads (config, candidates, batches) come
 * from `@ttc-spectree/toolbox-contract`; the rest mirror the API services.
 * Dates arrive as ISO 8601 strings. Every string that came from a tracker
 * item or a model is untrusted: render as text, never as HTML.
 */

interface DataResponse<T> {
  data: T;
}

// ---------------------------------------------------------------------------
// Projects and config
// ---------------------------------------------------------------------------

/** A project with grooming set up (`GET /grooming/projects`). */
export interface GroomingProjectSummary {
  tracker: "ado";
  project: string;
  teamName: string | null;
  groomTag: string;
  repoCount: number;
  updatedAt: string;
}

/**
 * The project's repeating grooming meeting (E280): on `weekday` (0 = Sunday
 * ... 6 = Saturday) every `everyNWeeks` weeks (1-4), counted from
 * `anchorDate` (`YYYY-MM-DD`), in the IANA `timeZone`.
 */
export interface GroomingMeetingPattern {
  weekday: number;
  everyNWeeks: number;
  anchorDate: string;
  timeZone: string;
  /**
   * Optional start time (PKG-PM-F): `HH:mm`, 24-hour wall clock in
   * `timeZone`. A pattern saved before it existed has none.
   */
  startTime?: string;
}

/**
 * A project's config. E280 adds the meeting pattern and the next meeting date
 * (`YYYY-MM-DD`); both are optional because the contract schema strips them.
 */
export type GroomingConfig = GroomingConfigResponse & {
  meetingPattern?: GroomingMeetingPattern | null;
  nextMeetingDate?: string | null;
  /**
   * The next meeting at the pattern's `startTime`, an ISO instant (PKG-PM-F);
   * null without a pattern or a start time. Optional because the contract
   * schema strips it.
   */
  nextMeetingStart?: string | null;
};

/**
 * `PUT /grooming/projects/:project/config`. `enabled` is optional (epic
 * PI-SPEC-SW-E288): the Repos page never sends it, so saving repositories
 * never turns grooming on; a new config starts off and the Admin turns it on
 * with the visible switch (`setEnabled`).
 */
export interface GroomingConfigInput {
  enabled?: boolean;
  repos: GroomingRepoInput[];
  teamName?: string | null;
  groomTag?: string | null;
}

/**
 * One ADO repository the Admin picks from (`GET …/ado-repositories`, epic
 * PI-SPEC-SW-E288, frame 1e), read with the caller's own PAT.
 */
export interface AdoRepository {
  /** ADO repository GUID (the branch list's key). */
  id: string;
  name: string;
  /** The ADO project that owns the repository. */
  project: string;
  /** Without `refs/heads/`; null for an empty repository. */
  defaultBranch: string | null;
  /** True when the project's config already maps this repository. */
  alreadyAdded: boolean;
}

/** One branch of an ADO repository (`GET …/ado-repositories/:repositoryId/branches`): default first. */
export interface AdoBranch {
  name: string;
  isDefault: boolean;
}

// ---------------------------------------------------------------------------
// Product areas
// ---------------------------------------------------------------------------

/** One plain-language part of the product, with how many recent briefs named it. */
export interface ProductArea {
  id: string;
  tracker: "ado";
  container: string;
  name: string;
  description: string;
  /** Where in the code, in plain words. */
  codeHint: string | null;
  sortOrder: number;
  /** `ai` until a person edits it. */
  source: ProductAreaSource;
  isArchived: boolean;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
  usedInBriefs: number;
  /** Name of the person who last changed the area (PKG-PM-F); null when unknown. */
  updatedByName: string | null;
}

export interface ProductAreaInput {
  name: string;
  description: string;
  codeHint?: string | null;
}

// ---------------------------------------------------------------------------
// Backlog view
// ---------------------------------------------------------------------------

/** What the room should do next with an item. */
export type GroomingNextAction = "ask-author" | "add-to-session" | "review-brief" | "ready";

/** The latest AI brief's size, always labelled as AI. */
export interface GroomingAiSuggestion {
  source: "ai";
  points: number;
  low: number;
  high: number;
  confidence: string;
  briefId: string;
  /** The item's content changed after the brief was written. */
  stale: boolean;
  commentStatus: string;
  createdAt: string;
}

/** The size shown for an item: the team's points win over the AI suggestion. */
export type GroomingSize = { source: "team"; points: number } | { source: "ai"; points: number; low: number; high: number };

/** One backlog row. */
export interface GroomingBacklogItem {
  itemId: string;
  ref: WorkItemRef;
  title: string;
  kind: WorkItemKind;
  typeName: string;
  state: string;
  iteration: string;
  backlogRank: number | null;
  /** When the item was created in ADO (ISO 8601); null when ADO did not report it (E280, PI-SPEC-SW-1406). */
  createdDate: string | null;
  priority: number | null;
  tags: string[];
  stage: GroomingStage;
  /** E280: `criteria` lists every grooming rule of the item's kind, present or missing. */
  readiness: { tier: ReadinessTier; missing: ReadinessMissing[]; criteria?: ReadinessCriterion[] };
  teamPoints: number | null;
  aiSuggestion: GroomingAiSuggestion | null;
  size: GroomingSize | null;
  hasCurrentBrief: boolean;
  nextAction: GroomingNextAction;
  /**
   * E283: someone is preparing this item in a running batch, or null.
   * Informational only: it never hides the row or disables an action.
   * Optional so rows from an older SpecTree still parse.
   */
  preparing?: GroomingPreparing | null;
  /**
   * E292 (UX-PM-12): the item's 1-based place in the team's backlog in ADO
   * order, whatever the sort; null for items outside the backlog iteration
   * (sprint exceptions) or beyond the read. Optional so rows from an older
   * SpecTree still parse.
   */
  adoPosition?: number | null;
  /** E292 (UX-PM-06): who created the item in ADO; null when ADO did not report it. */
  createdBy?: GroomingItemCreator | null;
  /** E292 (UX-PM-06, UX-PM-11): the latest time someone asked the item's author from SpecTree; null when never. */
  authorAsk?: GroomingAuthorAsk | null;
  /** ER-1656: short plain-text description (<= 160 chars); optional so an older SpecTree still parses. */
  snippet?: string | null;
}

/** E292: the item's creator in ADO (the person the author questions go to). */
export interface GroomingItemCreator {
  displayName: string;
  /** Sign-in name or e-mail; null when ADO did not report one. */
  uniqueName: string | null;
}

/** E292 (UX-PM-06): the item's latest author-questions post. */
export interface GroomingAuthorAsk {
  /** When the latest questions were posted (ISO 8601). */
  askedAt: string;
  askedById: string | null;
  /** Who posted them; null when that person's account was removed. */
  askedByName: string | null;
  /** How many times the author was asked. */
  askCount: number;
  /** True when the comment @mentioned the item's creator. */
  mentioned: boolean;
  mentionedName: string | null;
  /** The item's content changed after the latest ask (the estimate excluded). */
  changedSinceAsk: boolean;
  /** The item's last change (ISO 8601) when it changed since the ask; null otherwise. */
  itemChangedAt: string | null;
}

/** E283: who is preparing a backlog item in a running batch, and since when. */
export interface GroomingPreparing {
  /** The batch owner; null when their account was removed. */
  byUserId: string | null;
  byName: string | null;
  /** When the batch started (ISO 8601), or null. */
  startedAt: string | null;
  /**
   * E284 (grooming contract 3): whether the item is still waiting in that
   * person's run queue or being briefed now. Absent from an older SpecTree.
   */
  state?: "queued" | "running";
}

export interface GroomingPaceSprint {
  path: string;
  name: string;
  finishDate: string;
  completedPoints: number;
}

export interface GroomingRunway {
  groomedPoints: number;
  groomedItems: number;
  /** Mean completed points over the recent sprints; 0 when there are none. */
  pace: number;
  paceSprints: GroomingPaceSprint[];
  /** groomedPoints / pace, one decimal; null when pace is 0. */
  sprints: number | null;
  scoredItems: number;
  estimateOnlyItems: number;
}

/** A sprint: its path, display name and dates. */
export interface GroomingIteration {
  path: string;
  name: string;
  startDate: string;
  finishDate: string;
}

/** `GET /grooming/projects/:project/backlog`. */
export interface GroomingBacklog {
  project: string;
  team: string;
  backlogIterationPath: string;
  currentIteration: GroomingIteration | null;
  top: number | "all";
  /** Row order; the top-N cut was taken after sorting. */
  sort: GroomingBacklogSort;
  items: GroomingBacklogItem[];
  totalBacklogItems: number;
  truncated: boolean;
  stageCounts: Record<GroomingStage, number>;
  runway: GroomingRunway;
  /** Current-sprint items that are not groomed. Empty when there is no current sprint. */
  exceptions: GroomingBacklogItem[];
}

/** How many backlog rows to read: the top N, or all of them. */
export type GroomingBacklogTop = number | "all";

/**
 * Backlog row order (E280, PI-SPEC-SW-1406): newest or oldest created first,
 * or the team's ADO backlog order (`rank`). The API applies the top-N cut
 * after sorting and defaults to `rank` (E292, UX-PM-12).
 */
export const GROOMING_BACKLOG_SORTS = ["newest", "oldest", "rank"] as const;
export type GroomingBacklogSort = (typeof GROOMING_BACKLOG_SORTS)[number];

// ---------------------------------------------------------------------------
// Batches (session agenda and runs)
// ---------------------------------------------------------------------------

/** One row of `GET /grooming/batches?project=`. */
export interface GroomingBatchListEntry {
  id: string;
  tracker: "ado";
  container: string;
  mode: GroomingBatchMode;
  status: GroomingBatchStatus;
  tag: string | null;
  stopReason: GroomingStopReason | null;
  model: string | null;
  runnerHost: string | null;
  createdById: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** E280: the agenda's session date (`YYYY-MM-DD`), or null for the next meeting date. */
  sessionDate?: string | null;
  itemCount: number;
}

/**
 * A batch with its items. E280 adds `sessionDate` (an agenda's own meeting
 * date, `YYYY-MM-DD`) and `typicalItemSeconds` (median recent brief run in the
 * project, for the "usually N min" hint; null with too few runs). Optional:
 * the contract schema strips them, so older readers never see them.
 */
export type GroomingBatch = ContractGroomingBatch & {
  sessionDate?: string | null;
  typicalItemSeconds?: number | null;
};

export type CreateGroomingBatchResponse = Omit<ContractCreateGroomingBatchResponse, "batch"> & {
  batch: GroomingBatch;
};

export type { GroomingCandidatesResponse };

/**
 * `GET /grooming/projects/:project/session` (E280): the next session's shared
 * agenda (or null), the meeting pattern, the next meeting date, the session
 * date (the agenda's own date, else the next meeting date) and the typical
 * brief run time. Dates are `YYYY-MM-DD`.
 */
export interface GroomingProjectSession {
  project: string;
  batch: GroomingBatch | null;
  meetingPattern: GroomingMeetingPattern | null;
  nextMeetingDate: string | null;
  /** The pattern's next meeting at its start time, an ISO instant (PKG-PM-F); null without a start time. */
  nextMeetingStart: string | null;
  sessionDate: string | null;
  typicalItemSeconds: number | null;
}

/**
 * `POST /grooming/batches/:id/next-agenda` (PKG-PM-C, ER-1638): what carrying
 * a run's items to the next meeting's agenda did. `targetDate` is the
 * agenda's date (authoritative; screens preview it with
 * `lib/grooming/meeting-dates.ts`); pass `added` and `createdAgenda` to the
 * undo. API-only: not in `@ttc-spectree/toolbox-contract`.
 */
export interface NextAgendaResult {
  /** Null when a carry-over found nothing to carry and the project has no draft agenda (none is created). */
  agenda: GroomingBatch | null;
  targetDate: string | null;
  createdAgenda: boolean;
  added: string[];
  alreadyOnAgenda: string[];
  /** Eligible but past the 20-item cap. */
  leftOutFull: string[];
  leftOutBelowBar: string[];
  leftOutHasPoints: string[];
  notInRun: string[];
}

/** `POST /grooming/batches/:id/next-agenda/undo`: the agenda after the undo (null when it was canceled). */
export interface UndoNextAgendaResult {
  agenda: GroomingBatch | null;
  removed: string[];
  notOnAgenda: string[];
  canceled: boolean;
}

// ---------------------------------------------------------------------------
// Pilot metrics ("How grooming is going", epic PI-SPEC-SW-E293)
// ---------------------------------------------------------------------------

/** One agenda item of a finished session, in agenda order. */
export interface GroomingPrepItem {
  itemId: string;
  title: string;
  position: number;
  /** The batch item status when the session finished. */
  status: string;
  /** True exactly when status is `briefed` or `current`. */
  prepared: boolean;
}

/** One finished agenda (status `completed` or `stopped`) and how much of it was prepared. */
export interface GroomingPrepCoverageBatch {
  batchId: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** The meeting the agenda was for (`YYYY-MM-DD`); null when it used the computed next meeting date. */
  sessionDate: string | null;
  /** How many agenda items the batch had. */
  items: number;
  /** How many of them had a brief before the meeting. */
  briefedOrCurrent: number;
  /** Ratio, two decimals; never shown as a percentage. Null with no items. */
  coverage: number | null;
  agendaItems: GroomingPrepItem[];
}

export interface GroomingPrepCoverage {
  /** Finished agendas in the window, newest first. */
  batches: GroomingPrepCoverageBatch[];
  items: number;
  briefedOrCurrent: number;
  coverage: number | null;
}

/** Where the team's points landed against Claude's range. */
export type GroomingSizingLanded = "inside" | "above" | "below";

/**
 * One item the team has sized after a brief: Claude's range beside the
 * team's points. Claude's size reaches the web only through these rows, so
 * it is never shown before a meeting (01-roles.md section 9.4).
 */
export interface GroomingSizedItem {
  itemId: string;
  ref: WorkItemRef;
  /** From SpecTree's batch items; null for a brief saved outside a batch (show `AB#<id>`). */
  title: string | null;
  suggestedPoints: number;
  pointsLow: number;
  pointsHigh: number;
  teamPoints: number;
  teamPointsSeenAt: string | null;
  landed: GroomingSizingLanded;
  briefId: string;
  briefCreatedAt: string;
}

export interface GroomingEstimateAgreement {
  /** Sized items counted (`items.length`). */
  count: number;
  /** Of those, how many landed inside Claude's range. */
  withinRange: number;
  share: number | null;
  /** Newest `teamPointsSeenAt` first. */
  items: GroomingSizedItem[];
}

export type GroomingSprintUnavailableReason = "ADO_PAT_REQUIRED" | "ADO_PAT_INVALID";

/** `GET /grooming/projects/:project/metrics?sinceDays=` (`read-grooming-progress`). Advisory only. */
export interface GroomingMetrics {
  project: string;
  sinceDays: number;
  since: string;
  prepCoverage: GroomingPrepCoverage;
  estimateAgreement: GroomingEstimateAgreement;
  /** Current-sprint items that are not groomed; null when the sprint could not be read. */
  ungroomedSprintEntries: number | null;
  sprint: {
    currentIteration: GroomingIteration | null;
    unavailableReason: GroomingSprintUnavailableReason | null;
  };
}

function projectPath(project: string): string {
  return `/grooming/projects/${encodeURIComponent(project)}`;
}

function batchPath(batchId: string): string {
  return `/grooming/batches/${encodeURIComponent(batchId)}`;
}

function candidatesQuery(query: GroomingCandidatesQuery): string {
  const params = new URLSearchParams({ mode: query.mode });
  if (query.tag !== undefined) params.set("tag", query.tag);
  return params.toString();
}

export const groomingApi = {
  /** Projects with grooming set up. */
  projects: () => api.get<DataResponse<GroomingProjectSummary[]>>("/grooming/projects"),

  /** A project's config with a clone URL per repository. 404 GROOMING_CONFIG_NOT_FOUND when not set up. */
  config: (project: string) => api.get<DataResponse<GroomingConfig>>(`${projectPath(project)}/config`),

  /** Create or replace a project's config (`manage-repos`, Admin only since E288). */
  saveConfig: (project: string, input: GroomingConfigInput) =>
    api.put<DataResponse<GroomingConfig>>(`${projectPath(project)}/config`, input),

  /** The Grooming on/off switch (`manage-repos`, E288). Repositories stay set up either way. */
  setEnabled: (project: string, enabled: boolean) =>
    api.patch<DataResponse<GroomingConfig>>(`${projectPath(project)}/enabled`, { enabled }),

  /** The ADO repositories the Admin picks from (`manage-repos`, E288); `owner` lists another ADO project's. */
  adoRepositories: (project: string, owner?: string) =>
    api.get<DataResponse<AdoRepository[]>>(
      `${projectPath(project)}/ado-repositories${owner ? `?project=${encodeURIComponent(owner)}` : ""}`
    ),

  /** One ADO repository's branches, the default first (`manage-repos`, E288). */
  adoBranches: (project: string, repositoryId: string, owner?: string) =>
    api.get<DataResponse<AdoBranch[]>>(
      `${projectPath(project)}/ado-repositories/${encodeURIComponent(repositoryId)}/branches${
        owner ? `?project=${encodeURIComponent(owner)}` : ""
      }`
    ),

  /** Active product areas in sort order. */
  productAreas: (project: string) => api.get<DataResponse<ProductArea[]>>(`${projectPath(project)}/product-areas`),

  /** Add a person-written area (`map-project`). */
  createProductArea: (project: string, input: ProductAreaInput) =>
    api.post<DataResponse<ProductArea>>(`${projectPath(project)}/product-areas`, input),

  /** Edit an area; it becomes person-owned (`map-project`). */
  updateProductArea: (project: string, areaId: string, input: Partial<ProductAreaInput>) =>
    api.put<DataResponse<ProductArea>>(`${projectPath(project)}/product-areas/${encodeURIComponent(areaId)}`, input),

  /** Archive an area (`map-project`). Never a hard delete. */
  archiveProductArea: (project: string, areaId: string) =>
    api.delete<DataResponse<ProductArea>>(`${projectPath(project)}/product-areas/${encodeURIComponent(areaId)}`),

  /**
   * Undo a removal (`map-project`, PKG-PM-F): the archived area is active
   * again with `source`, the source it had before it was removed.
   */
  restoreProductArea: (project: string, areaId: string, source: ProductAreaSource) =>
    api.post<DataResponse<ProductArea>>(
      `${projectPath(project)}/product-areas/${encodeURIComponent(areaId)}/restore`,
      { source }
    ),

  /** Distinct IANA time zones of the people holding a Toolbox role on the project (`map-project`, PKG-PM-F). */
  teamTimeZones: (project: string) => api.get<DataResponse<string[]>>(`${projectPath(project)}/team-time-zones`),

  /** The team's backlog with stage, size, next action, runway and exceptions. */
  backlog: (project: string, top: GroomingBacklogTop, sort: GroomingBacklogSort) =>
    api.get<DataResponse<GroomingBacklog>>(
      `${projectPath(project)}/backlog?top=${encodeURIComponent(String(top))}&sort=${encodeURIComponent(sort)}`
    ),

  /** How grooming is going over the last `sinceDays` days (`read-grooming-progress`, E293). Read-only. */
  metrics: (project: string, sinceDays: number) =>
    api.get<DataResponse<GroomingMetrics>>(
      `${projectPath(project)}/metrics?sinceDays=${encodeURIComponent(String(sinceDays))}`
    ),

  /** Items that meet the grooming bar and have no current brief. */
  candidates: (project: string, query: GroomingCandidatesQuery) =>
    api.get<DataResponse<GroomingCandidatesResponse>>(`${projectPath(project)}/candidates?${candidatesQuery(query)}`),

  /** A project's batches, newest first. */
  batches: (project: string, limit = 20) =>
    api.get<DataResponse<GroomingBatchListEntry[]>>(
      `/grooming/batches?project=${encodeURIComponent(project)}&limit=${String(limit)}`
    ),

  /** One batch with its items and brief summaries. */
  batch: (batchId: string) => api.get<DataResponse<GroomingBatch>>(batchPath(batchId)),

  /** Create a draft batch. */
  createBatch: (body: CreateGroomingBatchBody) =>
    api.post<DataResponse<CreateGroomingBatchResponse>>("/grooming/batches", body),

  /** Add tracker items to the caller's draft batch. */
  addBatchItems: (batchId: string, itemIds: string[]) =>
    api.post<DataResponse<GroomingBatch>>(`${batchPath(batchId)}/items`, { itemIds }),

  /** Remove a tracker item from a draft batch (the caller's, or the shared agenda). */
  removeBatchItem: (batchId: string, itemId: string) =>
    api.delete<DataResponse<GroomingBatch>>(`${batchPath(batchId)}/items/${encodeURIComponent(itemId)}`),

  /** The next session: shared agenda, meeting pattern, next meeting date, session date, typical run time (E280). */
  getProjectSession: (project: string) => api.get<DataResponse<GroomingProjectSession>>(`${projectPath(project)}/session`),

  /** Set or clear (null) the team's repeating grooming meeting (`map-project`, E280/E283). */
  putMeetingPattern: (project: string, meetingPattern: GroomingMeetingPattern | null) =>
    api.put<DataResponse<GroomingConfig>>(`${projectPath(project)}/meeting-pattern`, { meetingPattern }),

  /** Set or clear (null) the shared draft agenda's session date, `YYYY-MM-DD` (any grooming user, E280). */
  patchBatchSessionDate: (batchId: string, sessionDate: string | null) =>
    api.patch<DataResponse<GroomingBatch>>(batchPath(batchId), { sessionDate }),

  /**
   * Carry a prepared agenda run's items to the next meeting's agenda
   * (`manage-agenda`, PKG-PM-C). The server keeps only items that still meet
   * the bar and have no team points; an empty list only starts the agenda.
   */
  nextAgenda: (runId: string, itemIds: string[]) =>
    api.post<DataResponse<NextAgendaResult>>(`${batchPath(runId)}/next-agenda`, { itemIds }),

  /** Undo a carry-over on the draft agenda it went to: pass the result's `added` and `createdAgenda`. */
  undoNextAgenda: (agendaId: string, body: { itemIds: string[]; cancelIfEmpty: boolean }) =>
    api.post<DataResponse<UndoNextAgendaResult>>(`${batchPath(agendaId)}/next-agenda/undo`, body),

  // FogBugz ↔ ADO project links (epic PI-SPEC-SW-E308, feature PI-SPEC-SW-1610)

  /** The project's FogBugz links and the overrides of other projects' links pointing into it (`map-project`). */
  fogbugzLinks: (project: string) =>
    api.get<DataResponse<FogbugzProjectLinksList>>(`${projectPath(project)}/fogbugz-links`),

  /**
   * Link a FogBugz project to this ADO project (`map-project` here and on
   * every override's ADO project). 409 FOGBUGZ_LINK_EXISTS when the FogBugz
   * project is linked already; 400 FOGBUGZ_LINK_AREA_PATH_NOT_FOUND for an
   * area path ADO does not have.
   */
  createFogbugzLink: (project: string, input: FogbugzProjectLinkInput) =>
    api.post<DataResponse<FogbugzProjectLinkView>>(`${projectPath(project)}/fogbugz-links`, input),

  /** Replace a link (`map-project` on every ADO project it targets, before and after). */
  updateFogbugzLink: (project: string, linkId: string, input: FogbugzProjectLinkInput) =>
    api.put<DataResponse<FogbugzProjectLinkView>>(
      `${projectPath(project)}/fogbugz-links/${encodeURIComponent(linkId)}`,
      input
    ),

  /** Remove a link (204; `map-project` on every ADO project it targets). */
  deleteFogbugzLink: (project: string, linkId: string) =>
    api.delete<unknown>(`${projectPath(project)}/fogbugz-links/${encodeURIComponent(linkId)}`),

  /**
   * The ADO project's area paths for the link editor's picker
   * (`map-project`, read with the caller's own ADO PAT; feature
   * PI-SPEC-SW-1644). 400 ADO_PAT_REQUIRED / ADO_PAT_INVALID without a usable
   * PAT. At most 2,000 paths; `truncated` when there were more.
   */
  adoAreaPaths: (project: string) =>
    api.get<DataResponse<AdoAreaPathList>>(`${projectPath(project)}/ado-area-paths`),
};

/**
 * The caller's open session agenda for a project: their newest draft batch in
 * `agenda` mode, or undefined. Only the owner may edit a batch, so a board
 * auditor's list (which holds everyone's) is filtered to their own.
 *
 * E279 rule, kept for the E279 pages: since E280 the project has one shared
 * draft agenda that any grooming user edits; read it with
 * `groomingApi.getProjectSession` (`useProjectSession`) instead.
 */
export function findOpenAgenda(
  batches: readonly GroomingBatchListEntry[] | undefined,
  userId: string | undefined
): GroomingBatchListEntry | undefined {
  if (!batches || !userId) return undefined;
  return batches.find((b) => b.mode === "agenda" && b.status === "draft" && b.createdById === userId);
}
